<template>
  <header class="ic-header" :class="{ 'ic-header--flat': !bordered }">
    <div class="ic-header__inner">
      <!-- Optional leading control (e.g. a back arrow on the listing detail
           page). Empty on every other page, so their layout is unchanged. -->
      <slot name="lead"></slot>

      <div class="ic-header__left">
        <router-link to="/" class="ic-logo">
          <img :src="logoSrc" :alt="logoAlt" />
        </router-link>
      </div>

      <!-- Desktop nav (≥1024px). Below that everything moves into the drawer.
           Le tre voci principali stanno al centro dell'header; "Agenzie"
           (per i professionisti) sta a destra, accanto al tasto Accedi. -->
      <nav class="ic-nav ic-nav--main" aria-label="Navigazione principale">
        <buy-nav-link />
        <sell-nav-link />
        <renovation-nav-link />
      </nav>

      <div class="ic-nav ic-nav--actions">
        <language-switcher class="ic-lang" />
        <agency-nav-link />

        <div
          class="ic-account"
          @click.stop
          @mouseenter="onAccountEnter"
          @mouseleave="onAccountLeave"
        >
          <button
            type="button"
            class="ic-account__btn"
            :class="{ 'has-avatar': isLoggedIn }"
            :aria-expanded="showAccount ? 'true' : 'false'"
            aria-haspopup="menu"
            @click="toggleAccount"
          >
            <span v-if="isLoggedIn" class="ic-account__avatar">
              <img v-if="avatarUrl" :src="avatarUrl" alt="" />
              <span v-else>{{ avatarInitials }}</span>
            </span>
            <span>{{ accountLabel }}</span>
            <ChevronDown :size="18" :class="{ 'is-open': showAccount }" />
          </button>
          <div v-if="showAccount" class="ic-account__menu" @mouseenter="onAccountEnter">
            <template v-if="isLoggedIn">
              <router-link
                v-for="item in globalStore.menuItems"
                :key="item.id"
                :to="item.route"
                class="ic-account__item"
                :class="{ 'is-active': isActiveRoute(item.route) }"
              >
                <img class="ic-account__icon" :src="require(`@/assets/img/vercel/${item.icon}`)" :alt="item.label" />
                <span>{{ item.label }}</span>
              </router-link>

              <div class="ic-account__sep"></div>

              <router-link
                v-for="item in globalStore.userMenuItems"
                :key="item.id"
                :to="item.route"
                class="ic-account__item"
                :class="{ 'is-active': isActiveRoute(item.route) }"
              >
                <img class="ic-account__icon" :src="require(`@/assets/img/vercel/${item.icon}`)" :alt="item.label" />
                <span>{{ item.label }}</span>
              </router-link>
              <button type="button" class="ic-account__item" @click="logout">
                <img class="ic-account__icon" src="@/assets/img/vercel/logout.svg" alt="Logout" />
                <span>Logout</span>
              </button>
            </template>
            <template v-else>
              <button type="button" class="ic-account__item" @click="goLogin">Accedi o registrati</button>
            </template>
          </div>
        </div>
      </div>

      <!-- Mobile/tablet (<1024px): hamburger that opens the full-screen drawer. -->
      <button
        ref="burger"
        type="button"
        class="ic-burger"
        :aria-expanded="drawerOpen ? 'true' : 'false'"
        aria-controls="ic-mobile-drawer"
        :aria-label="drawerOpen ? 'Chiudi il menu' : 'Apri il menu'"
        @click="drawerOpen ? closeDrawer() : openDrawer()"
      >
        <Menu :size="26" aria-hidden="true" />
      </button>
    </div>

    <!-- Drawer mobile: full-screen on phones, right-hand panel with tappable
         backdrop from ~420px of viewport spare (tablet). -->
    <transition name="ic-drawer">
      <div
        v-if="drawerOpen"
        id="ic-mobile-drawer"
        class="ic-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Menu di navigazione"
        @click.self="closeDrawer"
      >
        <div class="ic-drawer__panel h-screen-dvh">
          <div class="ic-drawer__head">
            <router-link to="/" class="ic-logo ic-drawer__logo" @click="closeDrawer">
              <img :src="logoSrc" :alt="logoAlt" />
            </router-link>
            <button
              ref="drawerClose"
              type="button"
              class="ic-drawer__close"
              aria-label="Chiudi il menu"
              @click="closeDrawer"
            >
              <X :size="26" aria-hidden="true" />
            </button>
          </div>

          <!-- Priorità in cima: il tasto "Accedi" (utente non loggato) o tutte
               le sezioni utente (loggato). Le pagine secondarie stanno in fondo. -->
          <div class="ic-drawer__account">
            <template v-if="isLoggedIn">
              <div class="ic-drawer__user">
                <span class="ic-account__avatar">
                  <img v-if="avatarUrl" :src="avatarUrl" alt="" />
                  <span v-else>{{ avatarInitials }}</span>
                </span>
                <span class="ic-drawer__username">{{ accountLabel }}</span>
              </div>

              <router-link
                v-for="item in globalStore.menuItems"
                :key="item.id"
                :to="item.route"
                class="ic-drawer__item"
                :class="{ 'is-active': isActiveRoute(item.route) }"
                :aria-current="isActiveRoute(item.route) ? 'page' : null"
                @click="closeDrawer"
              >
                <img class="ic-account__icon" :src="require(`@/assets/img/vercel/${item.icon}`)" :alt="item.label" />
                <span>{{ item.label }}</span>
              </router-link>

              <div class="ic-drawer__sep"></div>

              <router-link
                v-for="item in globalStore.userMenuItems"
                :key="item.id"
                :to="item.route"
                class="ic-drawer__item"
                :class="{ 'is-active': isActiveRoute(item.route) }"
                :aria-current="isActiveRoute(item.route) ? 'page' : null"
                @click="closeDrawer"
              >
                <img class="ic-account__icon" :src="require(`@/assets/img/vercel/${item.icon}`)" :alt="item.label" />
                <span>{{ item.label }}</span>
              </router-link>
              <button type="button" class="ic-drawer__item" @click="logout">
                <img class="ic-account__icon" src="@/assets/img/vercel/logout.svg" alt="Logout" />
                <span>Logout</span>
              </button>
            </template>
            <template v-else>
              <button type="button" class="ic-drawer__loginbtn" @click="goLogin">Accedi o registrati</button>
            </template>
          </div>

          <!-- Pagine secondarie ancorate in fondo, con lo stile delle voci
               profilo (non più in evidenza). Il click che risale chiude il
               drawer prima che ogni link esegua la sua navigazione/popup. -->
          <nav class="ic-drawer__nav" @click="closeDrawer">
            <buy-nav-link />
            <sell-nav-link />
            <renovation-nav-link />
            <agency-nav-link />
          </nav>

          <!-- La lingua del sito, anche su telefono. -->
          <div class="ic-drawer__lang">
            <language-switcher variant="drawer" />
          </div>

          <!-- #818: impostazioni cookie e documenti raggiungibili anche dalle
               pagine senza footer (area utente, preventivi). -->
          <div class="ic-drawer__legal" @click="closeDrawer">
            <legal-mini-links align="left" />
          </div>
        </div>
      </div>
    </transition>
  </header>
</template>

<script>
import { ChevronDown, Menu, X } from 'lucide-vue-next'
import { globalStore, _logoutUser } from '@/globalStore.js'
import { useViewport } from '@/composables/useViewport.js'
import AgencyNavLink from '@/components/AgencyNavLink.vue'
import BuyNavLink from '@/components/BuyNavLink.vue'
import RenovationNavLink from '@/components/RenovationNavLink.vue'
import SellNavLink from '@/components/SellNavLink.vue'
import LegalMiniLinks from '@/components/legal/LegalMiniLinks.vue'
import LanguageSwitcher from '@/components/LanguageSwitcher.vue'
import immaginaLogoSrc from '@/assets/img/newarc-logo-2025.png'
import newarcLogoSrc from '@/assets/img/vercel/logo-newarc.png'

// #859: how long the account menu stays open after the cursor leaves it.
export const ACCOUNT_CLOSE_DELAY_MS = 250

export default {
  name: 'ImmaginaHeader',
  components: { ChevronDown, Menu, X, AgencyNavLink, BuyNavLink, RenovationNavLink, SellNavLink, LegalMiniLinks, LanguageSwitcher },
  props: {
    bordered: { type: Boolean, default: true },
    brand: { type: String, default: 'immaginacasa' },
  },
  emits: ['login'],
  setup() {
    // lgUp = breakpoint desktop (≥1024px): oltre questa soglia il drawer non
    // esiste, quindi se resta aperto durante un resize va chiuso (watcher).
    const { lgUp } = useViewport()
    return { globalStore, lgUp }
  },
  data() {
    return {
      showAccount: false,
      // #859: pending hover-close, cancelled when the cursor comes back
      // (button or menu) before it fires.
      accountCloseTimer: null,
      drawerOpen: false,
      // Hover-capable pointer (desktop): keep the hover-open behaviour there,
      // touch devices only get the tap toggle (a synthetic mouseenter before
      // the tap's click would otherwise cancel the toggle).
      canHover:
        typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(hover: hover)').matches,
    }
  },
  computed: {
    logoSrc() {
      return this.brand === 'newarc' ? newarcLogoSrc : immaginaLogoSrc
    },
    logoAlt() {
      return this.brand === 'newarc' ? 'Newarc' : 'ImmaginaCasa.com'
    },
    isLoggedIn() {
      return globalStore.isLoggedIn
    },
    accountLabel() {
      if (globalStore.isLoggedIn && globalStore.newarcUser) {
        return globalStore.newarcUser.name
      }
      return 'Accedi'
    },
    avatarUrl() {
      return (globalStore.isLoggedIn && globalStore.newarcUser && globalStore.newarcUser.profilePicture) || ''
    },
    avatarInitials() {
      const u = globalStore.newarcUser
      if (!u) return ''
      return `${(u.name || '').charAt(0)}${(u.surname || '').charAt(0)}`.toUpperCase()
    },
  },
  watch: {
    // Tornati al layout desktop (resize/rotazione): il drawer non ha più senso.
    lgUp(isDesktop) {
      if (isDesktop && this.drawerOpen) this.closeDrawer()
    },
    // Navigazione da una voce del menu: chiudi drawer e dropdown account.
    $route() {
      if (this.drawerOpen) this.closeDrawer()
      this.closeAccount()
    },
    drawerOpen(open) {
      // Scroll-lock del body finché il drawer è aperto.
      if (typeof document !== 'undefined') {
        document.body.style.overflow = open ? 'hidden' : ''
      }
      if (open) {
        // Focus sulla X (primo controllo del dialog) all'apertura.
        this.$nextTick(() => {
          if (this.$refs.drawerClose) this.$refs.drawerClose.focus()
        })
      } else if (this.$refs.burger) {
        // Alla chiusura il focus torna all'hamburger che l'ha aperto.
        this.$refs.burger.focus()
      }
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown)
    // Tap/click fuori dal widget account: chiude il dropdown (il wrapper fa
    // @click.stop, quindi i click interni non arrivano fin qui).
    document.addEventListener('click', this.onDocumentClick)
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown)
    document.removeEventListener('click', this.onDocumentClick)
    this.cancelAccountClose()
    if (this.drawerOpen) document.body.style.overflow = ''
  },
  methods: {
    openDrawer() {
      this.drawerOpen = true
    },
    closeDrawer() {
      this.drawerOpen = false
    },
    onKeydown(e) {
      if (e.key !== 'Escape') return
      if (this.drawerOpen) this.closeDrawer()
      this.closeAccount()
    },
    onDocumentClick() {
      this.closeAccount()
    },
    closeAccount() {
      this.cancelAccountClose()
      this.showAccount = false
    },
    cancelAccountClose() {
      if (this.accountCloseTimer) {
        clearTimeout(this.accountCloseTimer)
        this.accountCloseTimer = null
      }
    },
    toggleAccount() {
      // Desktop (hover): l'hover ha già aperto il menu, il click lo conferma
      // e non lo richiude (si chiude con mouseleave/click fuori/Escape).
      // Touch: vero toggle, unico gesto disponibile.
      this.cancelAccountClose()
      this.showAccount = this.canHover ? true : !this.showAccount
    },
    onAccountEnter() {
      if (!this.canHover) return
      this.cancelAccountClose()
      this.showAccount = true
    },
    // #859: not on the spot. Leaving the button on the way to the menu (a
    // diagonal move, the menu being wider than the button) used to close it
    // before the cursor got there; a short delay, cancelled on re-entry,
    // lets it arrive.
    onAccountLeave() {
      if (!this.canHover) return
      this.cancelAccountClose()
      this.accountCloseTimer = setTimeout(() => {
        this.accountCloseTimer = null
        this.showAccount = false
      }, ACCOUNT_CLOSE_DELAY_MS)
    },
    // Sezione dashboard attiva: match esatto o per prefisso, così le route
    // figlie (/user/videochiamate/:id, /user/preventivi-ristrutturazione/add)
    // evidenziano la voce della sezione madre. $route può mancare nei test
    // montati senza router: in quel caso nessuna voce è attiva.
    isActiveRoute(route) {
      const path = this.$route && this.$route.path
      if (!path) return false
      return path === route || path.startsWith(route + '/')
    },
    goLogin() {
      this.closeAccount()
      this.closeDrawer()
      this.$emit('login')
    },
    async logout() {
      await _logoutUser()
      this.closeAccount()
      this.closeDrawer()
      // Su una pagina protetta (/user/*) il logout lascerebbe l'utente su una
      // vista che non può più usare (e il cui "Accedi" è un no-op perché quelle
      // route hanno requireAuth): si torna agli annunci, come fa la sidebar.
      if (this.$route && this.$router && this.$route.path.startsWith('/user')) {
        this.$router.push('/annunci')
      }
    },
  },
}
</script>

<style scoped>
.ic-header {
  position: sticky;
  top: 0;
  z-index: 40;
  background: var(--ic-bg, #f8f5f0);
  border-bottom: 1px solid #DDDDDD;
}
.ic-header--flat { border-bottom: none; }
.ic-header__inner {
  max-width: 1680px;
  margin: 0 auto;
  padding: 14px 40px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.ic-logo img { width: 190px; height: auto; object-fit: contain; object-position: left center; display: block; }

/* Logo on the left; space-between pushes the actions (Agenzie + account)
   to the right. */
.ic-header__left { display: flex; align-items: center; gap: 28px; }

.ic-nav { display: flex; align-items: center; gap: 8px; }
/* Compra / Vendi / Ristruttura centrate rispetto all'intero header (non allo
   spazio libero tra logo e azioni), indipendentemente dalla loro larghezza. */
.ic-nav--main {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

.ic-account { position: relative; }
.ic-account__btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 45px;
  padding: 0 20px;
  border-radius: 999px;
  background: var(--ic-surface, #ffffff);
  border: 1px solid #ECECEC;
  font-size: 16px;
  font-weight: 600 !important;
  color: var(--ic-ink, #1e1e1e);
  box-shadow: none;
  transition: background 0.18s ease;
}
.ic-account__btn.has-avatar { padding-left: 7px; }
.ic-account__avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #489B79;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
}
.ic-account__avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
.ic-account__btn:hover { background: #f3f1ec; }
.ic-account__btn svg { transition: transform 0.2s ease; color: var(--ic-ink, #1e1e1e); }
.ic-account__btn svg.is-open { transform: rotate(180deg); }
.ic-account__menu {
  position: absolute;
  right: 0;
  top: 100%;
  margin-top: 8px;
  min-width: 248px;
  background: #fff;
  border: 1px solid #DDDDDD;
  border-radius: 15px;
  padding: 8px;
  box-shadow: 0 12px 34px rgba(30, 30, 30, 0.12);
  display: flex;
  flex-direction: column;
}
/* Invisible bridge over the 8px gap so the cursor can travel from the
   button to the menu without the hover dropping (mouseleave). It overlaps
   the menu's border too (#859): no dead pixel between the two. */
.ic-account__menu::before {
  content: '';
  position: absolute;
  left: -1px;
  right: -1px;
  top: -10px;
  height: 11px;
}
.ic-account__item {
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 500;
  color: var(--ic-ink, #1e1e1e);
}
.ic-account__item:hover { background: #F6F6F6; }
/* Sezione dashboard corrente evidenziata (stessa logica della sidebar). */
.ic-account__item.is-active { color: var(--ic-green, #489B79); }
/* Monochrome vercel icons tinted grey (same glyphs as production). */
.ic-account__icon {
  width: 20px;
  height: 20px;
  flex: none;
  filter: brightness(0) saturate(100%) invert(22%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(95%) contrast(86%);
}
/* Verde #489B79 via filter, stesso valore di .menu-icon-active (vercel.css). */
.ic-account__item.is-active .ic-account__icon,
.ic-drawer__item.is-active .ic-account__icon {
  filter: brightness(0) saturate(100%) invert(55%) sepia(18%) saturate(1018%) hue-rotate(88deg) brightness(94%) contrast(86%);
}
/* Set Profilo + Logout apart from the navigation items above. */
.ic-account__sep {
  height: 1px;
  background: #ECECEC;
  margin: 6px 8px;
}

/* ---- Hamburger (nascosto su desktop) ---- */
.ic-burger {
  display: none;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  color: var(--ic-ink, #1e1e1e);
  /* Fondo sempre visibile (prima compariva solo al tap/hover). */
  background: #f2efe7;
  transition: background-color 0.18s ease;
}
.ic-burger:hover { background: #e8e3d8; }

/* ---- Drawer mobile ---- */
.ic-drawer {
  position: fixed;
  inset: 0;
  z-index: 60;
  background: rgba(30, 30, 30, 0.4);
  display: flex;
  justify-content: flex-end;
  font-family: 'Raleway', ui-sans-serif, system-ui, sans-serif;
}
/* Full-screen su tutti i telefoni (<640px): occupa l'intera larghezza, senza
   sliver di backdrop a sinistra anche sui phone più larghi (~430px). Sui
   tablet (≥640px) resta il pannello destro con backdrop tappabile. L'altezza
   arriva dall'utility globale .h-screen-dvh (100dvh con fallback 100vh). */
.ic-drawer__panel {
  width: 100%;
  background: var(--ic-bg, #f8f5f0);
  color: var(--ic-ink, #1e1e1e);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 0 24px calc(24px + env(safe-area-inset-bottom, 0px));
}
@media (min-width: 640px) {
  .ic-drawer__panel { width: min(420px, 100%); }
}
.ic-drawer__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 0;
  border-bottom: 1px solid #E3E0D8;
  margin-bottom: 10px;
}
.ic-drawer__logo img { width: 165px; }
.ic-drawer__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  color: var(--ic-ink, #1e1e1e);
  /* Fondo sempre visibile, come l'hamburger. */
  background: #f2efe7;
  transition: background-color 0.18s ease;
}
.ic-drawer__close:hover { background: #e8e3d8; }

/* Pagine secondarie (Compra / Vendi / Ristruttura / Agenzie): ancorate in fondo, sotto
   un separatore. Sono voci di secondo livello, quindi niente più priorità. */
.ic-drawer__nav {
  margin-top: auto;
  display: flex;
  flex-direction: column;
  padding-top: 18px;
  border-top: 1px solid #E3E0D8;
}
/* Le voci (Buy/Sell/Renovation/AgencyNavLink) prendono lo stesso stile di font
   delle voci profilo (.ic-drawer__item): lo scoped del padre raggiunge la
   root dei figli. */
.ic-drawer__nav .ic-nav__link {
  display: block;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  padding: 12px 0;
  border-radius: 0;
  color: var(--ic-ink, #1e1e1e);
  white-space: normal;
}
.ic-drawer__legal {
  padding-top: 14px;
}
.ic-drawer__lang {
  padding-top: 18px;
}
.ic-lang {
  margin-right: 6px;
}
.ic-drawer__nav .ic-nav__link:hover,
.ic-drawer__nav .ic-nav__link.router-link-exact-active { background: none; color: var(--ic-green, #489B79); }

/* Sezione account/login in cima al drawer (priorità), subito sotto la testata. */
.ic-drawer__account {
  display: flex;
  flex-direction: column;
  padding-top: 6px;
}
.ic-drawer__user {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 12px;
}
.ic-drawer__user .ic-account__avatar { width: 40px; height: 40px; font-size: 15px; }
.ic-drawer__username { font-size: 17px; font-weight: 700; }
.ic-drawer__item {
  display: flex;
  align-items: center;
  gap: 14px;
  text-align: left;
  padding: 12px 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--ic-ink, #1e1e1e);
}
.ic-drawer__item:hover { color: var(--ic-green, #489B79); }
.ic-drawer__item.is-active { color: var(--ic-green, #489B79); font-weight: 700; }
.ic-drawer__sep { height: 1px; background: #E3E0D8; margin: 8px 0; }
.ic-drawer__loginbtn {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 52px;
  border-radius: 999px;
  background: var(--ic-green, #489B79);
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  transition: background-color 0.18s ease;
}
.ic-drawer__loginbtn:hover { background: var(--ic-green-dark, #316C54); }

/* Slide-in da destra + fade del backdrop; niente animazione se l'utente
   preferisce ridurre il movimento. */
.ic-drawer-enter-active,
.ic-drawer-leave-active { transition: opacity 0.25s ease; }
.ic-drawer-enter-active .ic-drawer__panel,
.ic-drawer-leave-active .ic-drawer__panel { transition: transform 0.3s cubic-bezier(0.22, 0.61, 0.36, 1); }
.ic-drawer-enter-from,
.ic-drawer-leave-to { opacity: 0; }
.ic-drawer-enter-from .ic-drawer__panel,
.ic-drawer-leave-to .ic-drawer__panel { transform: translateX(100%); }
@media (prefers-reduced-motion: reduce) {
  .ic-drawer-enter-active,
  .ic-drawer-leave-active,
  .ic-drawer-enter-active .ic-drawer__panel,
  .ic-drawer-leave-active .ic-drawer__panel { transition: none; }
}

/* ---- Header compatto sotto il breakpoint desktop (lg 1024px) ---- */
@media (max-width: 1023.98px) {
  /* Le voci di navigazione migrano nel drawer. */
  .ic-nav { display: none; }
  .ic-burger { display: inline-flex; }
  /* Wordmark leggermente più grande: a 175px sta comodo anche a 320px di
     viewport accanto all'hamburger (44px) — lo swap alla sola icona non serve. */
  .ic-logo img { width: 175px; }
}

@media (max-width: 768px) {
  .ic-header__inner { padding-left: 20px; padding-right: 20px; }
}
</style>
